import React, { useState, useEffect } from 'react'
import { Container, Row, Col, Card, Spinner, Alert, Pagination, Button } from 'react-bootstrap'
import { Link, useSearchParams } from 'react-router-dom'
import http from '../../helpers/Request'
import { formatDate, stripHtml, truncateText } from '../../helpers/BlogText'
import service_bg from './../../assets/service_bg.png'
import { AnimationOnScroll } from 'react-animation-on-scroll'

const BlogList = () => {
  const [searchParams] = useSearchParams()
  const category = searchParams.get('category')
  const [blogs, setBlogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const itemsPerPage = 6
  const [categoryList, setCategoryList] = useState([])

  const fetchBlogs = async (page = 1) => {
    try {
      setLoading(true)
      let url = `/api/blog?page=${page}&limit=${itemsPerPage}`
      if (category) {
        url += `&category_slug=${category}`
      }
      const response = await http.get(url)
      
      setBlogs(response.data.data)
      setTotalPages(response.data.pagination.totalPages)
      setCurrentPage(page)
    } catch (err) {
      setError('Failed to load news')
      console.error('Error fetching news:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchCategory = async () => {
    try {
      const response = await http.get(`/api/blog_category`)
      
      setCategoryList(response.data.data)
    } catch (err) {
      console.error('Error fetching category:', err)
    }
  }
  
  useEffect(() => {
    fetchBlogs()
  }, [category])

  useEffect(() => {
    fetchCategory()
  }, [])

  const handlePageChange = (page) => {
    fetchBlogs(page)
    window.scrollTo(0, 0)
  }

  if (loading) {
    return (
      <div className="py-5">
        <Container>
          <div className="text-center">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-3">Loading news...</p>
          </div>
        </Container>
      </div>
    )
  }

  if (error) {
    return (
      <div className="py-5">
        <Container>
          <Alert variant="danger">{error}</Alert>
        </Container>
      </div>
    )
  }

  return (
    <div className="wrap-hidden">
      <div className='pb-5 pt-5'
        style={{
          backgroundImage: `url(${service_bg})`,
          position: "relative",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "top center",
          backgroundSize: "100% auto",
        }}
      >
        <Container className='mt-5 pt-5 pb-5'>
          <AnimationOnScroll animateOnce={true} animateIn="animate__fadeIn" duration={.8} animatePreScroll={true} offset={100}>
            <div className='mt-5 header text-center'><span className="text-brand-secondary">Siberwander</span> News</div>
            <p className='mt-4 text-justify text-center'><strong>Stay updated with the latest insights, news, and updates from SiberWander</strong></p>

            <div className='mt-5 d-flex flex-row gap-2 overflow-x-auto flex-nowrap' style={{scrollbarWidth: 'none', msOverflowStyle: 'none'}}>
              <Link
                to={`/news`}
                className={`btn ${!category?'btn-brand-secondary':'btn-outline-brand-secondary'} flex-shrink-0`}
              >
                All
              </Link>
              {
                categoryList.map((item, index) => (
                  <Link
                    key={index}
                    to={`/news?category=${item.slug}`}
                    className={`btn ${category===item.slug?'btn-brand-secondary':'btn-outline-brand-secondary'} flex-shrink-0`}
                  >
                    {item.title}
                  </Link>
                ))
              }
            </div>
          </AnimationOnScroll>
        </Container>
        
        <Container className='pb-5'>
          {blogs.length === 0 ? (
            <Row>
              <Col>
                <div className="text-center py-5">
                  <h3>No news available</h3>
                  <p className="text-muted">Check back later for new content!</p>
                </div>
              </Col>
            </Row>
          ) : (
            <>
              <Row>
                {blogs.map((blog) => (
                  <Col md={6} lg={4} key={blog.id} className="mb-4">
                    <Card className="h-100 shadow-sm">
                      {blog.image && (
                        <Card.Img
                          variant="top"
                          src={blog.image}
                          alt={blog.title}
                          style={{ height: '200px', objectFit: 'cover' }}
                          onError={(e) => {
                            e.target.style.display = 'none'
                          }}
                        />
                      )}
                      <Card.Body className="d-flex flex-column">
                        <Card.Title className="h5">{blog.title}</Card.Title>
                        <Card.Text className="text-muted flex-grow-1">
                          {truncateText(stripHtml(blog.content))}
                        </Card.Text>
                        <div className="mt-auto">
                          <small className="d-block mt-2">
                            <strong className='text-brand-secondary'>{blog.category}</strong>
                          </small>
                          <small className="d-block mb-2">
                            {blog.publish_date ? formatDate(blog.publish_date) : ""}
                          </small>
                          <Link
                            to={`/news/${blog.slug}`}
                            className="btn btn-outline-primary btn-sm"
                          >
                            Read More
                          </Link>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>

              {totalPages > 1 && (
                <Row>
                  <Col>
                    <div className="d-flex justify-content-center mt-4">
                      <Pagination>
                        <Pagination.Prev
                          disabled={currentPage === 1}
                          onClick={() => handlePageChange(currentPage - 1)}
                        />
                        {Array.from({ length: totalPages }, (_, i) => (
                          <Pagination.Item
                            key={i + 1}
                            active={i + 1 === currentPage}
                            onClick={() => handlePageChange(i + 1)}
                          >
                            {i + 1}
                          </Pagination.Item>
                        ))}
                        <Pagination.Next
                          disabled={currentPage === totalPages}
                          onClick={() => handlePageChange(currentPage + 1)}
                        />
                      </Pagination>
                    </div>
                  </Col>
                </Row>
              )}
            </>
          )}
        </Container>
      </div>
    </div>
  )
}

export default BlogList



